import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { Banknote, Download, QrCode, Wallet } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Kpi } from '@/components/ui/kpi'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'
import { inr, inrFull, localDate, localDateOffset } from '@/lib/format'
import { useLabel, useLabels } from '@/lib/labels'
import { ReceiptDrawer } from './ReceiptDrawer'

import {
  downloadRepaymentsXlsx,
  useCollections,
  useCollectionSummary,
  type CollectionFilters,
} from './api'

const CHANNELS = ['FIELD_CASH', 'UPI_QR', 'UPI_LINK'] as const
const STATUSES = ['CONFIRMED', 'PENDING', 'FAILED', 'EXPIRED'] as const

// Not a row status: COLLECTED means CONFIRMED + PENDING, i.e. money the agent
// actually took, banked or not. It is the default because field cash stays
// PENDING until the deposit is reconciled — filtering to CONFIRMED alone hid
// the entire cash channel, and left the Pending and Field cash tiles below
// reading zero no matter how much had been collected.
const COLLECTED = 'COLLECTED'

function today(): string {
  return localDate()
}

function daysAgo(n: number): string {
  return localDateOffset(-n)
}

/**
 * Collections — money in, across both channels, scoped to what you manage.
 *
 * The server applies hierarchy scoping, so a branch manager's totals are their
 * branch and an agent's are their own. Tiles and table come from the same
 * filters and the same server-side predicate; a tile that disagrees with the
 * rows under it is worse than no tile.
 */
export function CollectionsPage() {
  const label = useLabel()
  // The mode filter's options come from the label domain, not the payment-type
  // template endpoint: this page needs RECOVERY_VIEW, the template needs
  // PAYMENT_TYPE_VIEW, and a manager holds the first without the second.
  const { data: labels } = useLabels()
  const [openReceipt, setOpenReceipt] = useState<string | null>(null)
  const [from, setFrom] = useState(daysAgo(30))
  const [to, setTo] = useState(today())
  const [channel, setChannel] = useState<string | undefined>()
  const [mode, setMode] = useState<string | undefined>()
  const [status, setStatus] = useState<string | undefined>(COLLECTED)
  const [reconciled, setReconciled] = useState<boolean | undefined>()
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  // One object feeds the tiles, the table and the LMS export alike.
  const filters: CollectionFilters = useMemo(
    () => ({ from, to, channel, mode, status, reconciled }),
    [from, to, channel, mode, status, reconciled],
  )

  const summary = useCollectionSummary(filters)
  const rows = useCollections(filters, 200)

  async function onExport() {
    setExporting(true)
    setExportError(null)
    try {
      await downloadRepaymentsXlsx(filters)
    } catch (err) {
      // The server refuses rather than stream a file Excel cannot open; the
      // message names the fix (narrow the range), so surface it verbatim.
      const msg = isAxiosError(err)
        ? ((err.response?.data as { message?: string })?.message ?? err.message)
        : String(err)
      setExportError(msg)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-navy-900">Collections</h1>
          <p className="text-xs text-slate-500">
            Field cash and digital payments, scoped to what you manage.
          </p>
        </div>
        <Button onClick={onExport} disabled={exporting}>
          <Download className="mr-1.5 h-3.5 w-3.5" />
          {exporting ? 'Preparing…' : 'Export for LMS'}
        </Button>
      </div>

      {exportError && (
        <Alert>
          <AlertTitle>Could not build the export</AlertTitle>
          <AlertDescription>{exportError}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <div>
            <Label htmlFor="from">From</Label>
            <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="to">To</Label>
            <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="channel">Channel</Label>
            <select
              id="channel"
              value={channel ?? ''}
              onChange={(e) => setChannel(e.target.value || undefined)}
              className="block rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
            >
              <option value="">All channels</option>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {c.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="mode">Mode</Label>
            <select
              id="mode"
              value={mode ?? ''}
              onChange={(e) => setMode(e.target.value || undefined)}
              className="block rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
            >
              <option value="">All modes</option>
              {Object.entries(labels?.paymentMode ?? {}).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              value={status ?? ''}
              onChange={(e) => setStatus(e.target.value || undefined)}
              className="block rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
            >
              <option value="">All statuses</option>
              <option value={COLLECTED}>Collected (confirmed + pending)</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            {/* What still needs posting: Not reconciled + Confirmed is the next LMS file. */}
            <Label htmlFor="reconciled">LMS</Label>
            <select
              id="reconciled"
              value={reconciled === undefined ? '' : String(reconciled)}
              onChange={(e) => setReconciled(e.target.value === '' ? undefined : e.target.value === 'true')}
              className="block rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="false">Not reconciled</option>
              <option value="true">Reconciled</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Confirmed"
          value={inr(summary.data?.confirmedAmount ?? 0)}
          hint={`${summary.data?.confirmedCount ?? 0} collections`}
          loading={summary.isLoading}
          icon={<Wallet className="h-3.5 w-3.5" />}
        />
        <Kpi
          label="Pending"
          value={inr(summary.data?.pendingAmount ?? 0)}
          hint="awaiting deposit or settlement"
          loading={summary.isLoading}
        />
        <Kpi
          label="Field cash"
          value={inr(sliceAmount(summary.data?.byChannel, 'FIELD_CASH'))}
          loading={summary.isLoading}
          icon={<Banknote className="h-3.5 w-3.5" />}
        />
        <Kpi
          label="Digital"
          value={inr(
            sliceAmount(summary.data?.byChannel, 'UPI_QR') +
              sliceAmount(summary.data?.byChannel, 'UPI_LINK'),
          )}
          loading={summary.isLoading}
          icon={<QrCode className="h-3.5 w-3.5" />}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Collections</CardTitle>
          <CardDescription>
            {rows.data ? `${rows.data.length} shown` : 'Loading…'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rows.isLoading ? (
            <SkeletonRows rows={8} />
          ) : rows.data && rows.data.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">
              No collections in this range.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-2xs uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Loan</th>
                    <th className="py-2 pr-3">Customer</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Mode</th>
                    <th className="py-2 pr-3">Payment type</th>
                    <th className="py-2 pr-3">UTR / Ref no</th>
                    <th className="py-2 pr-3">Channel</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Collected by</th>
                    <th className="py-2 pr-3">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.data?.map((r) => (
                    <tr
                      key={r.collectionId}
                      onClick={() => setOpenReceipt(r.collectionId)}
                      title="Open the receipt"
                      className="cursor-pointer border-b border-slate-100 hover:bg-navy-50"
                    >
                      <td className="py-1.5 pr-3 font-mono text-xs">{r.loanAccountNo}</td>
                      <td className="py-1.5 pr-3">{r.customerName ?? '—'}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{inrFull(r.amount)}</td>
                      <td className="py-1.5 pr-3 tabular-nums">
                        {r.collectedAt ? r.collectedAt.slice(0, 10) : '—'}
                      </td>
                      <td className="py-1.5 pr-3">{label('paymentMode', r.mode)}</td>
                      {/* "—" is a real answer: a legacy receipt that named only a
                          channel more than one type allows is stored with none. */}
                      <td className="py-1.5 pr-3">{r.paymentTypeName ?? '—'}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs">{r.referenceNo ?? '—'}</td>
                      <td className="py-1.5 pr-3">{r.channel.replace(/_/g, ' ')}</td>
                      <td className="py-1.5 pr-3">
                        <Badge>{r.status}</Badge>
                      </td>
                      <td className="py-1.5 pr-3">
                        {r.empId ? `${r.empId} · ${r.agentName ?? ''}` : '—'}
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-xs">
                        {r.gatewayReference ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      {openReceipt && <ReceiptDrawer collectionId={openReceipt} onClose={() => setOpenReceipt(null)} />}
    </div>
  )
}

function sliceAmount(slices: { key: string; amount: number }[] | undefined, key: string): number {
  return slices?.find((s) => s.key === key)?.amount ?? 0
}
